/**
 * Shared enum option lists for the 7 connector connection/notification-channel forms (Charter M3
 * migration). Each `{value, label}` array backs both the rendered `<Select>` `<option>` list AND
 * the corresponding Zod schema's `z.enum()` tuple, so the UI options and the validated value set
 * can never drift apart. Mirrors the `agentFormFields.tsx` / `mcpServerFormFields.tsx` pattern.
 */
import type {
  AzureDevOpsConnection,
  GitHubConnection,
  GitLabConnection,
  JiraAuthType,
  JiraDeploymentType,
  NotificationChannelType,
  ServiceNowConnection,
} from '../types';

export const GITHUB_INSTALLATION_TYPE_OPTIONS: { value: GitHubConnection['installationType']; label: string }[] = [
  { value: 'PersonalAccessToken', label: 'Personal Access Token (Classic)' },
  { value: 'GitHubApp', label: 'GitHub App Installation' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
];

export const GITLAB_AUTH_TYPE_OPTIONS: { value: GitLabConnection['authType']; label: string }[] = [
  { value: 'PersonalAccessToken', label: 'Personal Access Token' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
  { value: 'DeployToken', label: 'Deploy Token' },
];

export const JIRA_DEPLOYMENT_TYPE_OPTIONS: { value: JiraDeploymentType; label: string }[] = [
  { value: 'Cloud', label: 'Jira Cloud' },
  { value: 'Server', label: 'Jira Server' },
  { value: 'DataCenter', label: 'Jira Data Center' },
];

export const JIRA_AUTH_TYPE_OPTIONS: { value: JiraAuthType; label: string }[] = [
  { value: 'ApiToken', label: 'API Token' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
  { value: 'BasicAuth', label: 'Basic Auth' },
];

export const AZURE_DEVOPS_AUTH_TYPE_OPTIONS: { value: AzureDevOpsConnection['authType']; label: string }[] = [
  { value: 'PersonalAccessToken', label: 'Personal Access Token' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
  { value: 'ServicePrincipal', label: 'Service Principal' },
];

export const SERVICENOW_AUTH_TYPE_OPTIONS: { value: ServiceNowConnection['authType']; label: string }[] = [
  { value: 'Basic', label: 'Basic Authentication' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
];

export const NOTIFICATION_CHANNEL_TYPE_OPTIONS: { value: NotificationChannelType; label: string }[] = [
  { value: 'Slack', label: 'Slack' },
  { value: 'MicrosoftTeams', label: 'Microsoft Teams' },
  { value: 'Webhook', label: 'Webhook' },
  { value: 'Email', label: 'Email' },
];

/**
 * Matches the original hand-rolled validators' `new URL(value)` try/catch exactly — deliberately
 * NOT restricted to http/https (unlike `mcp-governance`'s `isAbsoluteHttpUrl`), since none of the
 * pre-migration connector forms enforced a protocol.
 */
export function isParseableUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}
